import { restrictChallengeInterfacePaste } from '@/challenges/clipboardInterfaces';
import { ownTileMetaAssignment } from '@/models/tiles/SignalSides';
import { chipBudget } from '@/challenges/chips';
import { runtimeGraphIds } from '@/models/logic/RuntimeGraphIds';
import { allowsChallengePlacement, allowsChallengeTile } from '@/challenges/editing';
import { clipboardForCircuit } from '@/utils/structureClipboard';
import { isValidTilePosition } from '@/models/TileManagerModel';
import { Action, ActionType } from '@/models/HistoryModel';
import { TileInstance } from '@/models/tiles/TileInstance';
import { deepClone } from '@/utils/clone';
import { Point, type PointData } from '@/models/Point';
import type { IChipEditorModel, ICircuitModel } from '@/interfaces/models';
import type Context from '@/context';
import {
  buildConnectedSidesMap,
  parseClipChipRef,
  type ClipboardData,
} from '@/models/clipboard/ClipboardData';
import type { Connection, ConnectionPoint } from '@/models/connections/ConnectionGraph';
import { CircuitType, oppositeSide, Side, sideOffsets } from '@/schemas/common';
import { removeInterfaceTileFromChipModel } from '@/actions/tileActions';
import { tileDefinitionRegistry, isInterfaceTile } from '@/models/tiles/TileRegistry';
import { getEffectiveSideIO } from '@/models/tiles/TileDefinition';
import { TileSideIOType, type InterfaceTileState } from '@/schemas/tiles';
import { ChipInstanceModel } from '@/models/ChipInstanceModel';
import { Rotation, rotateSide } from '@/models/data/Rotation';
import { InterfaceLink, InterfaceType } from '@/models/ChipModel';
import type { InterfaceLinkInstanceId } from '@/types/ids';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { TileType } from '@/models/TileTypes';

export function createPasteAction(
  context: Context,
  clipboardData: ClipboardData,
  pasteOrigin: PointData,
  circuitModel: ICircuitModel,
  chipEditor: IChipEditorModel,
  options: { connectNeighbors?: boolean; preferCopiedInterfaceSettings?: boolean } = {},
): Action | null {
  clipboardData = clipboardForCircuit(
    clipboardData,
    circuitModel.circuitType,
    chipEditor.chipModel.kind === 'bundle-adapter',
  );
  clipboardData = restrictChallengeInterfacePaste(context, clipboardData);
  if (
    clipboardData.tiles.length === 0 ||
    clipboardData.tiles.some(
      (tile) =>
        !isValidTilePosition(
          pasteOrigin.x + tile.relativeLocation.x,
          pasteOrigin.y + tile.relativeLocation.y,
        ),
    )
  )
    return null;

  const tileManager = circuitModel.tileManagerModel;
  const connectionModel = circuitModel.connectionModel;

  const fits = (entry: ClipboardData['tiles'][number]) =>
    allowsChallengePlacement(context, tileManager, {
      x: pasteOrigin.x + entry.relativeLocation.x,
      y: pasteOrigin.y + entry.relativeLocation.y,
    });
  // Primitive pastes clip at the border; placed chips remain indivisible.
  const clippedChips = new Set(
    clipboardData.tiles
      .filter((entry) => !fits(entry) && entry.meta.partOfChip)
      .map((entry) => parseClipChipRef(entry.meta.partOfChip!)),
  );
  if (context.challenge)
    for (const chip of clipboardData.chipInstances)
      if (!chipBudget(context, chip.chipId)?.allowed) clippedChips.add(chip.localIndex);
  const retainedChipIndices = new Set(
    clipboardData.chipInstances
      .filter(
        (chip) =>
          !clippedChips.has(chip.localIndex) && context.chipStore.getChip(chip.chipId),
      )
      .map((chip) => chip.localIndex),
  );
  clipboardData = {
    ...clipboardData,
    tiles: clipboardData.tiles.filter(
      (entry) =>
        fits(entry) &&
        ((!!entry.meta.partOfChip &&
          retainedChipIndices.has(parseClipChipRef(entry.meta.partOfChip)!)) ||
          allowsChallengeTile(context, tileManager, entry.tileType, entry.state)) &&
        !(
          entry.meta.partOfChip &&
          clippedChips.has(parseClipChipRef(entry.meta.partOfChip))
        ),
    ),
    chipInstances: clipboardData.chipInstances.filter(
      (chip) => !clippedChips.has(chip.localIndex),
    ),
  };
  if (!clipboardData.tiles.length) return null;

  let placedTiles: TileInstance[] = [];
  let appliedConnections: Connection[] = [];
  let appliedEdgeConnections: Connection[] = [];
  let placedChipInstances: ChipInstanceModel[] = [];

  return {
    type: ActionType.AddTiles,
    description: 'Paste',
    do: () => {
      placedTiles = [];
      appliedConnections = [];
      appliedEdgeConnections = [];
      placedChipInstances = [];

      // Determine which chip instances can be fully placed (all tiles fit)
      const blockedChipIndices = new Set<number>();
      for (const tileEntry of clipboardData.tiles) {
        const ref = tileEntry.meta.partOfChip as string | null;
        const idx = ref ? parseClipChipRef(ref) : null;
        if (idx === null) continue;

        const absPoint = new Point(
          pasteOrigin.x + tileEntry.relativeLocation.x,
          pasteOrigin.y + tileEntry.relativeLocation.y,
        );
        if (tileManager.has(absPoint)) {
          blockedChipIndices.add(idx);
        }
      }

      // Create chip instances for pasted chips (skip blocked ones)
      const localIndexToChipInstance = new Map<number, ChipInstanceModel>();

      for (const clipChip of clipboardData.chipInstances) {
        if (blockedChipIndices.has(clipChip.localIndex)) continue;
        const chipModel = context.chipStore.chips.find((c) => c.id === clipChip.chipId);
        if (!chipModel) continue;

        const absOrigin = new Point(
          pasteOrigin.x + clipChip.relativeOrigin.x,
          pasteOrigin.y + clipChip.relativeOrigin.y,
        );

        const editingChipInstanceId =
          chipEditor.chipModel.editingInstance?.id ??
          context.editor.currentChip.editingInstance.id;

        if (!chipModel.templateGraph) {
          chipModel.rebuildTemplateGraph();
        }
        const cloneResult = chipModel.templateGraph?.clone();
        const logicGraph = cloneResult?.graph ?? new LogicGraph(runtimeGraphIds(context));

        const chipInstance = new ChipInstanceModel(
          context,
          chipModel.id,
          absOrigin,
          logicGraph,
          {
            chipInstanceId: editingChipInstanceId,
            location: absOrigin,
            circuitType: circuitModel.circuitType,
          },
        );

        chipInstance.rotation = clipChip.rotation ?? Rotation.CW_0;
        chipInstance.bundleWidth = clipChip.bundleWidth;

        if (cloneResult) {
          chipInstance.templateToInstanceIdMap = cloneResult.idMap;
        }

        circuitModel.chips.push(chipInstance);
        context.project?.chipInstanceMap?.set(chipInstance.id, chipInstance);
        chipEditor.chipModel.logicCircuit.chipInstances.set(
          chipInstance.id,
          chipInstance,
        );

        localIndexToChipInstance.set(clipChip.localIndex, chipInstance);
        placedChipInstances.push(chipInstance);
      }

      // Map from relative position key to placed tile
      const relPosToTile = new Map<string, TileInstance>();

      // Place tiles, skipping occupied cells and tiles belonging to blocked chips
      for (const entry of clipboardData.tiles) {
        if (
          !entry.meta.partOfChip &&
          !allowsChallengeTile(context, tileManager, entry.tileType, entry.state)
        )
          continue;

        const entryChipRef = entry.meta.partOfChip as string | null;
        const entryChipIndex = entryChipRef ? parseClipChipRef(entryChipRef) : null;
        if (entryChipIndex !== null && blockedChipIndices.has(entryChipIndex)) continue;

        const absX = pasteOrigin.x + entry.relativeLocation.x;
        const absY = pasteOrigin.y + entry.relativeLocation.y;
        const absPoint = new Point(absX, absY);

        if (tileManager.has(absPoint)) continue;

        const chipInstanceId =
          chipEditor.chipModel.editingInstance?.id ??
          context.editor.currentChip.editingInstance.id;
        const tile = new TileInstance(context, entry.tileType, {
          chipInstanceId,
          location: absPoint,
          circuitType: circuitModel.circuitType,
        });

        const meta = deepClone(entry.meta);

        // Resolve chip instance reference
        const clipChipRef = meta.partOfChip as string | null;
        const localIndex = clipChipRef ? parseClipChipRef(clipChipRef) : null;
        if (localIndex !== null) {
          const chipInstance = localIndexToChipInstance.get(localIndex);
          if (chipInstance) {
            meta.partOfChip = chipInstance.id;
          } else {
            meta.partOfChip = null;
          }
        } else {
          meta.partOfChip = null;
        }
        tile.meta = ownTileMetaAssignment(tile, meta);

        if (entry.state !== undefined) {
          tile.state = deepClone(entry.state) as typeof tile.state;
        }

        // Update interface tile state for chip tiles
        const linkedBundlePort =
          tile.tileType === TileType.BundleInterface &&
          Boolean((tile.state as InterfaceTileState<unknown>).connectedInterface);
        if (meta.partOfChip && (isInterfaceTile(tile) || linkedBundlePort)) {
          const interfaceState = tile.state as InterfaceTileState<unknown>;
          interfaceState.connectedInterface.chipInstanceId = meta.partOfChip;
          interfaceState.connectedInterface.circuitType = CircuitType.ChipLogic;
        }

        if (linkedBundlePort)
          tile.effectiveIoSides = {
            ...tileDefinitionRegistry.get(tile.tileType).ioSides,
            [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
          };

        if (!meta.partOfChip && (isInterfaceTile(tile) || linkedBundlePort)) {
          const state = tile.state as InterfaceTileState<unknown>;
          let originalLink = chipEditor.chipModel.interfaceLinkMap.get(
            state.connectedInterface.interfaceLinkId as InterfaceLinkInstanceId,
          );
          const movingWithinChip =
            clipboardData.operation === 'cut' &&
            state.connectedInterface.chipInstanceId === chipInstanceId;
          if (
            movingWithinChip &&
            !originalLink &&
            entry.interfaceLink &&
            !chipEditor.chipModel.getInterfaceLinkByLabel(entry.interfaceLink.label)
          ) {
            originalLink = InterfaceLink.fromDto(entry.interfaceLink);
            chipEditor.chipModel.interfaceLinkMap.set(originalLink.id, originalLink);
          }
          const reuseLink =
            movingWithinChip &&
            originalLink &&
            !originalLink.tiles[circuitModel.circuitType];
          // A standalone duplicate owns a new pin. Never register it against
          // the original link, which would replace that circuit's original tile.
          state.connectedInterface = {
            chipInstanceId,
            circuitType:
              circuitModel.circuitType === CircuitType.ChipLogic
                ? CircuitType.ChipStructure
                : CircuitType.ChipLogic,
            interfaceLinkId: '',
          };
          if (reuseLink) {
            state.connectedInterface.interfaceLinkId = originalLink!.id;
            if (originalLink!.type === InterfaceType.MULTI)
              tile.effectiveIoSides = originalLink!.computeEffectiveIoSides();
          } else if (originalLink || entry.interfaceLink) {
            const settings = options.preferCopiedInterfaceSettings
              ? (entry.interfaceLink ?? originalLink!.dto)
              : (originalLink?.dto ?? entry.interfaceLink!);
            const fresh = chipEditor.chipModel.createInterfaceLink(settings.type);
            const copied = InterfaceLink.fromDto({
              ...settings,
              id: fresh.id,
              label:
                chipEditor.chipModel.kind === 'bundle-adapter'
                  ? settings.label
                  : fresh.label,
            });
            chipEditor.chipModel.interfaceLinkMap.set(copied.id, copied);
            state.connectedInterface.interfaceLinkId = copied.id;
            if (copied.type === InterfaceType.MULTI)
              tile.effectiveIoSides = copied.computeEffectiveIoSides();
          }
        }

        tileManager.add(tile);
        placedTiles.push(tile);

        const relKey = `${entry.relativeLocation.x},${entry.relativeLocation.y}`;
        relPosToTile.set(relKey, tile);

        // Handle standalone interface tiles (not part of chip)
        if (!meta.partOfChip && chipEditor) {
          const tileDefinition = tileDefinitionRegistry.get(tile.tileType);
          tileDefinition?.eventHandlers?.placed?.({
            instance: tile,
            chipEditorModel: chipEditor,
          });
        }
      }

      // Stable signal IDs describe a local physical network. Rebind ownership
      // only when the source owner itself was successfully included in this paste.
      const sourceOwners = new Map<string, TileInstance>();
      for (const entry of clipboardData.tiles) {
        if (!entry.sourceTileId || entry.meta.bundle?.signalOrigin !== entry.sourceTileId)
          continue;
        const tile = relPosToTile.get(
          `${entry.relativeLocation.x},${entry.relativeLocation.y}`,
        );
        if (tile) sourceOwners.set(entry.sourceTileId, tile);
      }
      for (const tile of placedTiles) {
        const origin = tile.meta.bundle?.signalOrigin;
        const owner = origin && sourceOwners.get(origin);
        if (owner && tile.meta.bundle) tile.meta.bundle.signalOrigin = owner.id;
      }

      // Admit ordinary paste's connection phases together. The top-level batch
      // flushes before explicit disconnections, which must win over copied edges.
      const restoreInternalConnections = () => {
        for (const conn of clipboardData.connections) {
          const relKeyA = `${conn.a.x},${conn.a.y}`;
          const relKeyB = `${conn.b.x},${conn.b.y}`;
          const tileA = relPosToTile.get(relKeyA);
          const tileB = relPosToTile.get(relKeyB);

          if (!tileA || !tileB) continue;

          const pointA: ConnectionPoint = { tileId: tileA.id, side: conn.a.side };
          const pointB: ConnectionPoint = { tileId: tileB.id, side: conn.b.side };

          connectionModel.connect(pointA, pointB);
          appliedConnections.push({ a: pointA, b: pointB });
        }
      };
      if (connectionModel.batchConnections)
        connectionModel.batchConnections(restoreInternalConnections);
      else restoreInternalConnections();

      for (const edge of clipboardData.disconnected ?? []) {
        const a = relPosToTile.get(`${edge.a.x},${edge.a.y}`);
        const b = relPosToTile.get(`${edge.b.x},${edge.b.y}`);
        if (a && b)
          connectionModel.disconnect(
            { tileId: a.id, side: edge.a.side },
            { tileId: b.id, side: edge.b.side },
            { preventReconnect: true },
          );
      }

      // Build connected sides map: which sides were originally connected per tile
      const connectedSidesMap = buildConnectedSidesMap(clipboardData);
      const placedTileSet = new Set(placedTiles);

      // Connect pasted tiles to pre-existing neighbors, only on originally-connected sides.
      const restoreNeighborConnections = () => {
        for (const entry of clipboardData.tiles) {
          const relKey = `${entry.relativeLocation.x},${entry.relativeLocation.y}`;
          const tile = relPosToTile.get(relKey);
          if (!tile) continue;

          const connectedSides = connectedSidesMap.get(relKey);
          if (!connectedSides) continue;

          for (const side of connectedSides) {
            const worldSide = rotateSide(side, tile.meta.rotation ?? Rotation.CW_0);
            const offset = sideOffsets[worldSide];
            const neighborPoint = new Point(
              tile.location.x + offset.dx,
              tile.location.y + offset.dy,
            );
            const neighborTile = tileManager.get(neighborPoint);

            if (!neighborTile) continue;
            // Skip other pasted tiles — internal connections already handled
            if (placedTileSet.has(neighborTile)) continue;

            const neighborSide = rotateSide(
              oppositeSide(worldSide),
              ((4 - (neighborTile.meta.rotation ?? 0)) % 4) as Rotation,
            );
            const tileDef = tileDefinitionRegistry.get(tile.tileType);
            const neighborDef = tileDefinitionRegistry.get(neighborTile.tileType);
            const tileSideIO = getEffectiveSideIO(tile, side, tileDef);
            const neighborSideIO = getEffectiveSideIO(
              neighborTile,
              neighborSide,
              neighborDef,
            );

            if (
              tileSideIO.type === TileSideIOType.NONE ||
              neighborSideIO.type === TileSideIOType.NONE
            ) {
              continue;
            }

            const pointA: ConnectionPoint = { tileId: tile.id, side };
            const pointB: ConnectionPoint = {
              tileId: neighborTile.id,
              side: neighborSide,
            };

            connectionModel.connect(pointA, pointB);
            appliedEdgeConnections.push({ a: pointA, b: pointB });
          }
        }
      };
      if (options.connectNeighbors !== false) {
        if (connectionModel.batchConnections)
          connectionModel.batchConnections(restoreNeighborConnections);
        else restoreNeighborConnections();
      }
    },
    undo: () => {
      // Disconnect edge connections
      for (const conn of appliedEdgeConnections) {
        connectionModel.disconnect(conn.a, conn.b);
      }
      appliedEdgeConnections = [];

      // Disconnect internal connections
      for (const conn of appliedConnections) {
        connectionModel.disconnect(conn.a, conn.b);
      }
      appliedConnections = [];

      // Remove placed tiles
      for (const tile of placedTiles) {
        tileManager.remove(tile);
        if (!tile.meta.partOfChip && chipEditor) {
          removeInterfaceTileFromChipModel(tile, chipEditor);
        }
      }
      placedTiles = [];

      // Remove chip instances
      for (const chipInstance of placedChipInstances) {
        const index = circuitModel.chips.indexOf(chipInstance);
        if (index >= 0) {
          circuitModel.chips.splice(index, 1);
        }
        chipEditor.chipModel.logicCircuit.chipInstances.delete(chipInstance.id);
        context.project?.chipInstanceMap?.delete(chipInstance.id);
      }
      placedChipInstances = [];
    },
  };
}
